import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './WaitlistModal.css';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WaitlistModal: React.FC<WaitlistModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle mounting/unmounting
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      onClose();
    }, 300); // Match animation duration
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isClosing) {
        handleClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isClosing, handleClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    handleClose();
    setEmail('');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isClosing) {
      handleClose();
    }
  };

  if (!shouldRender) return null;

  const modal = (
    <div 
      className={`waitlist-modal-backdrop ${isClosing ? 'closing' : ''}`} 
      onClick={handleBackdropClick}
    >
      <div className={`waitlist-modal-content ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button className="waitlist-modal-close" onClick={handleClose} aria-label="Close">
          ×
        </button>
        <h2 className="waitlist-modal-title">Join our waitlist</h2>
        <p className="waitlist-modal-caption">
          Be the first to find out when we launch the beta version of Wrap
        </p>
        <form onSubmit={handleSubmit} className="waitlist-modal-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="waitlist-modal-input"
            required
          />
          <button type="submit" className="waitlist-modal-submit">
            Submit
          </button>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default WaitlistModal;

